import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, LogOut } from 'lucide-react';
import { Button } from './button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';

interface SessionExpiryWarningProps {
  remainingSeconds: number;
  warningThreshold?: number; // seconds before expiry to show warning
  onStayLoggedIn: () => void;
  onLogout: () => void;
}

export function SessionExpiryWarning({
  remainingSeconds,
  warningThreshold = 60,
  onStayLoggedIn,
  onLogout,
}: SessionExpiryWarningProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Show modal when remaining time is below threshold
  useEffect(() => {
    if (remainingSeconds <= warningThreshold && remainingSeconds > 0) {
      setIsOpen(true);
    } else if (remainingSeconds > warningThreshold) {
      setIsOpen(false);
    }
  }, [remainingSeconds, warningThreshold]);

  const handleStayLoggedIn = () => {
    setIsOpen(false);
    onStayLoggedIn();
  };

  const handleLogout = () => {
    setIsOpen(false);
    onLogout();
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Clock className="h-5 w-5 animate-pulse" />
            Session Expiring Soon
          </DialogTitle>
          <DialogDescription className="pt-2">
            Your session will expire in{' '}
            <span className="font-bold text-destructive">
              {remainingSeconds} seconds
            </span>{' '}
            due to inactivity. Would you like to stay logged in?
          </DialogDescription>
        </DialogHeader>

        {/* Countdown visual */}
        <div className="flex justify-center py-4">
          <motion.div
            className="relative w-24 h-24"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            {/* Background circle */}
            <svg className="w-24 h-24 transform -rotate-90">
              <circle
                cx="48"
                cy="48"
                r="44"
                fill="none"
                stroke="hsl(var(--muted))"
                strokeWidth="6"
              />
              {/* Progress circle */}
              <motion.circle
                cx="48"
                cy="48"
                r="44"
                fill="none"
                stroke="hsl(var(--destructive))"
                strokeWidth="6"
                strokeLinecap="round"
                initial={{ pathLength: 1 }}
                animate={{
                  pathLength: remainingSeconds / warningThreshold,
                }}
                transition={{ duration: 0.5, ease: 'linear' }}
                style={{
                  strokeDasharray: '276.46',
                  strokeDashoffset: '0',
                }}
              />
            </svg>
            {/* Center text */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl font-bold text-destructive">
                {remainingSeconds}
              </span>
            </div>
          </motion.div>
        </div>

        <DialogFooter className="flex gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={handleLogout}
            className="flex-1 gap-2"
          >
            <LogOut className="h-4 w-4" />
            Log Out
          </Button>
          <Button
            onClick={handleStayLoggedIn}
            className="flex-1 gap-2"
            autoFocus
          >
            <Clock className="h-4 w-4" />
            Stay Logged In
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}